import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchShipments, fetchShipmentsWithFilters, DEFAULT_SHIPMENT_PAGE_SIZE, editShipmentData } from './store/slice';
import { fetchStatuses } from '../../core/redux/status/slice';
import { fetchCustomers } from '../../core/redux/customer/slice';
import ShipmentTopHead from './shipment-top-head';
import ShipmentContent from './shipment-content';
import ShipmentModals from './shipment-modals';
import { actions as shipmentActions } from './store/slice';
import { useLocation } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import { msalInstance } from '../../auth/msal';
import { httpInterceptor } from '../../service/http-interceptor';
import { showToast } from 'Core/redux/toast/slice';
import { showModal } from 'Core/redux/main/slice';
import { MODAL_TYPES } from '../components/app-modals';
import { ShipmentService } from '../../service/api/shipment-service';
import { parseShipmentDeepLink } from './shipment-deep-link';

const { setCustomer } = shipmentActions;
const { SHIPMENT_ADD_EDIT } = MODAL_TYPES;

const ShipmentView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const openedDeepLinkKeyRef = useRef(null);

  // Check if we are in customer dashboard shipment page
  const isCustomerDashboard = location.pathname === all_routes.shipments;

  useEffect(() => {
    const initializeData = async () => {
      try {
        // First, load customers data
        await dispatch(fetchCustomers({}));

        if (isCustomerDashboard) {
          let companyId = localStorage.getItem('Company');

          // If Company is not set (e.g. returning CCAMS user whose AuthCallback
          // already ran in a previous session), resolve it now via getbydomain.
          if (!companyId || !localStorage.getItem('CustomerType')) {
            try {
              const accounts = msalInstance.getAllAccounts();
              const email = accounts?.[0]?.username || '';
              const domain = email.includes('@') ? email.split('@')[1] : '';
              const isExternal = domain && domain.toLowerCase() !== 'chemplastinc.com';

              // Check if user has a CCAMS role from stored id token claims
              let hasCcamsRole = false;
              try {
                const raw = sessionStorage.getItem('MSAL_LAST_IDTOKEN_CLAIMS');
                if (raw) {
                  const claims = JSON.parse(raw);
                  const roles = Array.isArray(claims?.roles) ? claims.roles : [];
                  hasCcamsRole = roles.some((r) => r === 'CCAMS_Cus' || r === 'CCAMS_Logi');
                }
              } catch (_e) { }

              if (isExternal && hasCcamsRole) {
                console.log('ShipmentView: Calling getbydomain for', domain);
                const apiRes = await httpInterceptor.get(`Customer/getbydomain?domain=${domain}`);
                if (apiRes && apiRes.response) {
                  const resolvedId = apiRes.response.id || apiRes.response.Id;
                  if (resolvedId) {
                    localStorage.setItem('Company', resolvedId);
                    companyId = resolvedId;
                    console.log('ShipmentView: Resolved companyId', companyId);
                  }
                  // Always save CustomerType (even if empty) from the response
                  const resolvedCustomerType = apiRes.response.CustomerType || apiRes.response.customerType || '';
                  localStorage.setItem('CustomerType', resolvedCustomerType);
                  console.log('ShipmentView: Resolved CustomerType', resolvedCustomerType);
                }
              }
            } catch (e) {
              console.error('ShipmentView: getbydomain lookup failed', e);
            }
          }

          const customerType = localStorage.getItem('CustomerType') || '';

          if (companyId) {
            dispatch(setCustomer({ value: companyId }));
            dispatch(
              fetchShipments({
                customer: companyId,
                customerType,
                pageNumber: 1,
                pageSize: DEFAULT_SHIPMENT_PAGE_SIZE,
                append: false,
              }),
            );
          } else {
            console.warn('ShipmentView: Customer dashboard — no companyId resolved, fetching all');
            dispatch(
              fetchShipments({
                customerType,
                pageNumber: 1,
                pageSize: DEFAULT_SHIPMENT_PAGE_SIZE,
                append: false,
              }),
            );
          }
        } else {
          dispatch(fetchShipmentsWithFilters());
        }

        // Load statuses
        dispatch(fetchStatuses());
      } catch (error) {
        console.error('Error initializing shipment data:', error);
      }
    };

    initializeData();

    // Do not reset shipment filters here: React Strict Mode remounts this view and would
    // clear header `searchText` before the debounced getall refetch runs, so part search
    // looked like a no-op. Filters reset when leaving the app / signing out if needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const deepLink = parseShipmentDeepLink(location.search);
    if (!deepLink) {
      openedDeepLinkKeyRef.current = null;
      return undefined;
    }

    const key = `${deepLink.shipmentId || ''}:${deepLink.asp || ''}`;
    if (openedDeepLinkKeyRef.current === key) {
      return undefined;
    }

    let cancelled = false;
    const openShipment = async () => {
      const fail = () => {
        if (!cancelled) {
          dispatch(
            showToast({
              message: 'Could not open the shipment from this notification.',
              type: 'danger',
            }),
          );
        }
      };

      try {
        let shipment = null;

        if (deepLink.shipmentId) {
          const result = await dispatch(editShipmentData(deepLink.shipmentId));
          if (cancelled) return;
          if (editShipmentData.fulfilled.match(result) && (result.payload?.id || result.payload?.Id)) {
            shipment = result.payload;
          }
        }

        if (!shipment && deepLink.asp) {
          const response = await ShipmentService.getShipmentByAsp(deepLink.asp);
          if (cancelled) return;
          const byAsp = response?.response;
          const aspId = byAsp?.id ?? byAsp?.Id;
          if (response?.status === 200 && aspId) {
            const result = await dispatch(editShipmentData(aspId));
            if (cancelled) return;
            if (editShipmentData.fulfilled.match(result) && (result.payload?.id || result.payload?.Id)) {
              shipment = result.payload;
            } else {
              shipment = byAsp;
            }
          }
        }

        if (cancelled) return;
        if (!shipment) {
          fail();
          return;
        }

        dispatch(showModal(SHIPMENT_ADD_EDIT, shipment));
        openedDeepLinkKeyRef.current = key;
      } catch {
        fail();
      }
    };

    openShipment();
    return () => {
      cancelled = true;
    };
  }, [dispatch, location.search]);

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Shipments</h4>
              </div>
            </div>
            <ShipmentTopHead />
          </div>
          <ShipmentContent />
          <ShipmentModals />
        </div>
      </div>
    </div>
  );
};

export default ShipmentView;
